"use client";

import React from "react";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const wp_query_params = {
  per_page: 4,
  status: "publish",
  order: "asc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
function GiftsOfHopeConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => <Card key={i} data-index={i} className="h-[400px] animate-pulse bg-card/40 border-border/30">
            <div className="h-48 bg-muted/50 m-6 [border-radius:40%_60%_70%_30%/40%_50%_60%_50%]" />
            <CardHeader className="space-y-2">
              <div className="h-6 bg-muted/50 rounded w-3/4 mx-auto" />
              <div className="h-4 bg-muted/50 rounded w-1/4 mx-auto" />
            </CardHeader>
            <CardContent>
              <div className="h-16 bg-muted/50 rounded w-full" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 text-muted-foreground">
        <p>Unable to load gifts at this time. Please try again later.</p>
      </div>;
  }
  if (!products || products.length === 0) {
    return <div className="text-center py-12 text-muted-foreground">
        <p>No gifts are currently available.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
      {products.map((product, i) => <Card key={product.id} data-index={i} className="flex flex-col h-full bg-card/60 backdrop-blur-md border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden group">
          <div className="p-6 pb-2 flex justify-center items-center relative">
            <div className="absolute inset-0 bg-primary/5 blur-2xl rounded-full transform group-hover:scale-110 transition-transform duration-500" />
            
            <Link to={product.link} className="block relative z-10 w-48 h-48">
              <div className="w-full h-full overflow-hidden bg-muted [border-radius:40%_60%_70%_30%/40%_50%_60%_50%] shadow-inner transition-transform duration-500 group-hover:scale-105">
                <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </Link>
          </div>

          <CardHeader className="text-center pb-2 px-6">
            <Link to={product.link}>
              <CardTitle className="font-serif text-2xl text-primary hover:text-primary/80 transition-colors line-clamp-2">
                {product.title}
              </CardTitle>
            </Link>
            {product.price !== undefined && <div className="mt-2 text-lg font-medium text-primary/90">
                ${product.price.toFixed(2)}
              </div>}
          </CardHeader>

          <CardContent className="text-center flex-grow px-6">
            {product.short_description ? <div className="text-sm text-muted-foreground font-sans line-clamp-3 [&>p]:mb-0" dangerouslySetInnerHTML={{
          __html: product.short_description
        }} /> : <p className="text-sm text-muted-foreground font-sans">
                A meaningful gift to support our mission of hope and healing.
              </p>}
          </CardContent>

          <CardFooter className="pt-4 pb-6 px-6 mt-auto">
            <AddToCartButton productId={product.id} className="w-full" buttonClassName="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-md hover:shadow-lg transition-all py-6 text-base font-medium tracking-wide" text="Donate this Gift" showQuantity={false} />
          </CardFooter>
        </Card>)}
    </div>;
}
export default function GiftsOfHope() {
  return <section id="giftsofhope" className="relative py-24 bg-gradient-to-b from-background via-secondary/20 to-background overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute top-[60%] -right-[10%] w-[40%] h-[60%] rounded-full bg-accent/10 blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-6 tracking-tight">
            Gifts of Hope
          </h2>
          <p className="text-lg text-muted-foreground font-sans leading-relaxed">
            Choose a specific gift to directly support the children and families at The Story Haven. 
            Each item provides essential tools for creative expression, emotional resilience, and healing.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 4,
        status: ["publish"],
        order: "asc",
        orderby: "date"
      }}>
          <GiftsOfHopeConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}